import { ImageUploadModal } from "@/components/avatar-upload/ImageUploadModal.tsx";
import { RecognitionBadge } from "@/components/recognition-badge/RecognitionBadge.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useRecognitionCategories, useRecognitionSave } from "@/queries/use-recognitions.query.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiFilePathTypes } from "@/types/file.types.ts";
import { ApiRecognition } from "@/types/recognitions/recognition.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Input,
  Modal,
  ModalProps,
  Select,
  Stack,
  Text,
  Textarea,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface RecognitionEditModalProps extends ModalProps {
  categoryId?: number;
  recognition?: ApiRecognition;
}

type RecognitionArt = z.infer<typeof artSchema> | null;

function recognitionToArt(recognition?: ApiRecognition): RecognitionArt {
  if (recognition?.badge_file_uuid) {
    return { uuid: recognition.badge_file_uuid, url: recognition.badge_url };
  }
  return null;
}

const artSchema = z.object({ uuid: z.string(), url: z.string().nullable() });

export function RecognitionEditModal({
  categoryId,
  recognition,
  ...props
}: RecognitionEditModalProps) {
  const { t } = useTranslation();
  const { openModal, close } = useModals();
  const { id } = props;

  const { data: categories } = useRecognitionCategories();

  const categoryOptions = useMemo(
    () => (categories ?? []).map((category) => ({ id: category.id, title: category.title })),
    [categories]
  );

  const required = t("errors:field_required");

  const recognitionSchema = z
    .object({
      category_id: z.number({ error: required }),
      title: z.string({ error: required }).trim().min(1, required),
      description: z.string(),
      art: artSchema.nullable(),
    })
    .refine((data) => data.art !== null || Boolean(recognition?.system_icon_code), {
      message: t("recognition:art_required"),
      path: ["art"],
    });
  type RecognitionSchema = z.infer<typeof recognitionSchema>;

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RecognitionSchema>({
    mode: "onSubmit",
    resolver: zodResolver(recognitionSchema),
    defaultValues: {
      category_id: recognition?.category_id ?? categoryId,
      title: recognition?.title ?? "",
      description: recognition?.description ?? "",
      art: recognitionToArt(recognition),
    },
  });

  const [isDropdownOpen, setDropdownOpen] = useState(false);

  const recognitionSave = useRecognitionSave(control);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await recognitionSave.mutateAsync({
        id: recognition?.id,
        payload: {
          category_id: data.category_id,
          title: data.title.trim(),
          description: data.description.trim(),
          system_icon_code: data.art ? null : (recognition?.system_icon_code ?? null),
          badge_file_uuid: data.art?.uuid ?? null,
        },
      });
      close(id);
    } catch {}
  });

  return (
    <Modal
      {...props}
      title={
        recognition
          ? t("recognition:recognition_edit_title")
          : t("recognition:recognition_create_title")
      }
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            isLoading={recognitionSave.isPending}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"2xl"}>
          <Controller
            control={control}
            name={"art"}
            render={({ field }) => {
              const art = field.value;
              const previewTemplate = {
                title: recognition?.title ?? "",
                badge_url: art?.url ?? null,
                system_icon_code: art ? null : (recognition?.system_icon_code ?? null),
              };

              return (
                <Stack gap={"sm"}>
                  <DropdownMenu
                    open={isDropdownOpen}
                    closeOnClick={false}
                    onOpenChange={setDropdownOpen}
                    trigger={
                      <Box
                        className={"relative w-41 h-41 group cursor-pointer rounded-sm shadow-lg"}
                      >
                        <RecognitionBadge template={previewTemplate} size={"md"} />
                        <Box
                          className={
                            "bg-dark/20 opacity-0 group-hover:opacity-100 absolute inset-0 rounded-sm flex items-center justify-center"
                          }
                        >
                          <FontAwesomeIcon
                            icon={"fa-light fa-camera"}
                            className={"text-[40px] text-light"}
                          />
                        </Box>
                      </Box>
                    }
                  >
                    <DropdownMenuItem
                      leftIcon={<FontAwesomeIcon icon={"fa-light fa-image"} />}
                      onClick={() => {
                        openModal(ImageUploadModal, {
                          modalTitle: t("recognition:field_badge"),
                          path: ApiFilePathTypes.recognition_badge,
                          withCrop: true,
                          generatePublicLink: true,
                          onSave: async (file: ApiFile) => {
                            field.onChange({
                              uuid: file.uuid,
                              url: file.url_permanent || null,
                            });
                            return true;
                          },
                        });
                        setDropdownOpen(false);
                      }}
                      title={t("common:upload_new")}
                    />
                    {art && (
                      <DropdownMenuItem
                        variant={"menuDanger"}
                        leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                        onClick={() => {
                          field.onChange(null);
                          setDropdownOpen(false);
                        }}
                        title={t("common:delete")}
                      />
                    )}
                  </DropdownMenu>
                  {errors.art?.message && (
                    <Text className={"text-danger text-sm"}>{errors.art.message}</Text>
                  )}
                </Stack>
              );
            }}
          />
          <Stack gap={"md"}>
            <Input
              required={true}
              label={t("common:title")}
              size={"lg"}
              autoFocus={true}
              type={"text"}
              {...register("title")}
              error={errors.title?.message}
            />
            <Textarea
              label={t("common:description")}
              {...register("description")}
              error={errors.description?.message}
            />
            <Controller
              control={control}
              name={"category_id"}
              render={({ field }) => (
                <Select
                  label={t("recognition:field_category")}
                  emptyTitle={t("recognition:field_category_placeholder")}
                  options={categoryOptions}
                  value={field.value}
                  onChange={(value) => field.onChange(value as number)}
                  error={errors.category_id?.message}
                />
              )}
            />
          </Stack>
        </Stack>
      </form>
    </Modal>
  );
}
